import React, { useEffect, useMemo, useState } from 'react';
import { MultiSelect } from 'primereact/multiselect';
import DropdownItem from '../../../../../shared/components/dropdown-item/DropdownItem';
import { t } from 'i18next';
import { InputText } from 'primereact/inputtext';
import { KeywordsData, TagsResponse } from '../../../../../shared/models/tags/TagsResponse';
import { useForm } from 'react-hook-form';
import { Button } from 'primereact/button';

interface EditTagDialogProps {
  currentTags: TagsResponse[];
  projectId: number;
  tag: TagsResponse | null;
  onSubmit: (data: Partial<TagsResponse>) => void;
  keywordsOptions: KeywordsData[];
}

const EditTagDialog: React.FC<EditTagDialogProps> = ({
  currentTags,
  projectId,
  tag,
  onSubmit,
  keywordsOptions,
}) => {
  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm<Partial<TagsResponse>>({
    defaultValues: {
      label: tag?.label || '',
    },
  });

  const [selectedKeywords, setSelectedKeywords] = useState<KeywordsData[]>([]);
  const [keywordsError, setKeywordsError] = useState<string | null>(null);

  const availableKeywordsOptions = useMemo(() => {
    const usedKeywordIds = new Set<number>();

    currentTags.forEach((t) => {
      if (tag && t.id === tag.id) return;

      t.keywordsData?.forEach((kw) => {
        if (kw.id) {
          usedKeywordIds.add(kw.id);
        }
      });
    });

    return keywordsOptions.filter((opt) => !usedKeywordIds.has(opt.id));
  }, [currentTags, keywordsOptions, tag]);

  useEffect(() => {
    if (tag?.keywordsData && keywordsOptions?.length > 0) {
      const matched = keywordsOptions.filter((opt) =>
        tag.keywordsData.some((k) => k.id === opt.id || k.keyword === opt.keyword)
      );
      setSelectedKeywords(matched);
    } else {
      setSelectedKeywords([]);
    }

    if (tag?.label) {
      setValue('label', tag.label);
    }
  }, [tag, keywordsOptions, setValue]);

  const handleFormSubmit = (data: Partial<TagsResponse>) => {
    if (currentTags.find((t) => t.label === data.label) && tag === null) {
      setKeywordsError(t('conflictTagsName'));
      return;
    }

    if (selectedKeywords.length === 0) {
      setKeywordsError(t('fieldIsRequired'));
      return;
    }

    setKeywordsError(null);

    onSubmit({
      ...data,
      id: tag ? tag.id : undefined,
      projectId,
      keywordsData: selectedKeywords,
    });
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="form">
      <div className="form-box__row-block">
        <div className="form-box__label">{t('tagLabel')}:</div>
        <InputText
          {...register('label', { required: t('fieldIsRequired') })}
          className={errors.label ? 'form-box__invalid' : ''}
        />
        {errors.label && <span>{errors.label.message as string}</span>}
      </div>

      <div className="form-box__row-block">
        <div className="form-box__label">{t('keywords')}:</div>
        <MultiSelect
          value={selectedKeywords}
          onChange={(e) => {
            setSelectedKeywords(e.value);
            setKeywordsError(e.value.length === 0 ? t('fieldIsRequired') : null);
          }}
          options={availableKeywordsOptions}
          optionLabel="keyword"
          itemTemplate={(option: KeywordsData) => <DropdownItem name={option.keyword} />}
          maxSelectedLabels={1}
          showSelectAll={false}
          placeholder={t('selectBrands')}
          filter
          className={`dropdown dropdown--multi-select ${keywordsError ? 'form-box__invalid' : ''}`}
        />
        {keywordsError && <span>{keywordsError}</span>}
      </div>
      <Button className="btn btn--sm" type="submit" label={t(tag ? 'saveChanges' : 'addTag')} />
    </form>
  );
};

export default EditTagDialog;
